import { useState, useMemo, useEffect, useCallback } from 'react';
import ProjectPhotoImage from '@/components/base/ProjectPhotoImage';
import InlineUploadDropzone from '@/components/feature/InlineUploadDropzone';
import type { DailyLogReviewPhoto } from '@/hooks/useManagementDailyLog';
import {
  deletePmDailyLogPhoto,
  loadPmDailyLogUploads,
  resolveDailyLogIdForClientShare,
  saveDailyLogClientDraft,
  shareDailyLogWithClient,
  uploadPmDailyLogPhoto,
  type PmDailyLogUploadRow,
} from '@/lib/dailyLogClientShare';
import {
  copyDailyLogShareError,
  formatDailyLogShareError,
  formatOperationLabel,
  isValidDailyLogId,
  JPH_APP_BUILD_LABEL,
  logDailyLogShareDebug,
  logShareQueryDebug,
  shareError,
  validatePhotoRecord,
  type DailyLogShareDebugSnapshot,
  type DailyLogShareError,
} from '@/lib/dailyLogShareDiagnostics';
import { resolveFileDisplayUrl, useSignedFileUrls } from '@/lib/storageSignedUrl';

const SHARE_TIMEOUT_MS = 60_000;

interface PrepareDailyLogForClientModalProps {
  open: boolean;
  projectId: string;
  projectName: string;
  logDate: string;
  dateLabel: string;
  photos: DailyLogReviewPhoto[];
  pmUserId: string;
  onClose: () => void;
  onSuccess: () => void;
  onPhotosChanged?: () => void;
}

type Step = 'edit' | 'preview';

function setShareError(
  setter: (value: DailyLogShareError | null) => void,
  error: DailyLogShareError | undefined,
  fallbackOperation: DailyLogShareError['operation'],
  fallbackMessage: string
) {
  if (error) {
    setter(error);
    return;
  }
  setter({
    operation: fallbackOperation,
    message: fallbackMessage,
  });
}

export default function PrepareDailyLogForClientModal({
  open,
  projectId,
  projectName,
  logDate,
  dateLabel,
  photos,
  pmUserId,
  onClose,
  onSuccess,
  onPhotosChanged,
}: PrepareDailyLogForClientModalProps) {
  const [step, setStep] = useState<Step>('edit');
  const [initializing, setInitializing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const [operationError, setOperationError] = useState<DailyLogShareError | null>(null);
  const [copyOk, setCopyOk] = useState(false);
  const [migrationWarning, setMigrationWarning] = useState('');
  const [originalReport, setOriginalReport] = useState('');
  const [dailyLogId, setDailyLogId] = useState<string | null>(null);
  const [clientVersionStatus, setClientVersionStatus] = useState<string | null>(null);
  const [clientSharedAt, setClientSharedAt] = useState<string | null>(null);
  const [pmClientComment, setPmClientComment] = useState('');
  const [selectedPhotoIds, setSelectedPhotoIds] = useState<Set<string>>(new Set());
  const [pmUploads, setPmUploads] = useState<PmDailyLogUploadRow[]>([]);
  const [pmUploadLoading, setPmUploadLoading] = useState(false);
  const [pmUploadErrors, setPmUploadErrors] = useState<string[]>([]);
  const [pmDeleteError, setPmDeleteError] = useState('');
  const [pmDeletingId, setPmDeletingId] = useState<string | null>(null);
  const [showDebug, setShowDebug] = useState(false);

  const subcontractorPhotos = useMemo(
    () => photos.filter((p) => p.review_status !== 'rejected'),
    [photos]
  );

  const subcontractorPhotoKey = useMemo(
    () => subcontractorPhotos.map((p) => `${p.id}:${p.review_status || ''}:${p.file_url || ''}`).join('|'),
    [subcontractorPhotos]
  );

  const subcontractorPhotoValidation = useMemo(() => {
    const map = new Map<string, { valid: boolean; reason?: string }>();
    for (const photo of subcontractorPhotos) {
      const result = validatePhotoRecord(photo);
      map.set(photo.id, { valid: result.valid, reason: result.reason });
    }
    return map;
  }, [subcontractorPhotos]);

  const subcontractorPhotoUrls = useSignedFileUrls(subcontractorPhotos);

  const pmUploadRefs = useMemo(
    () =>
      pmUploads.map((p) => ({
        id: p.id,
        file_url: p.url,
        storage_bucket: p.storage_bucket ?? null,
        storage_path: p.storage_path ?? null,
      })),
    [pmUploads]
  );
  const pmUploadDisplayUrls = useSignedFileUrls(pmUploadRefs);

  function buildDefaultSelectedIds(
    availablePhotos: DailyLogReviewPhoto[],
    clientStatus?: string | null
  ): Set<string> {
    const selectable = availablePhotos.filter((p) => validatePhotoRecord(p).valid);
    const visibleByReview = selectable
      .filter((p) => p.review_status === 'approved_for_client')
      .map((p) => p.id);
    if (visibleByReview.length > 0) {
      return new Set(visibleByReview);
    }

    if (clientStatus === 'shared') {
      return new Set(
        selectable.filter((p) => p.review_status !== 'internal_only').map((p) => p.id)
      );
    }

    return new Set(selectable.map((p) => p.id));
  }

  const refreshPmUploads = useCallback(async (logId: string) => {
    logShareQueryDebug('refreshPmUploads', { dailyLogId: logId, projectId, logDate });

    if (!isValidDailyLogId(logId)) {
      const err = shareError(
        'load_photos',
        `Daily log id is missing or invalid (${String(logId)}). Cannot load PM uploads.`,
        { dailyLogId: typeof logId === 'string' ? logId : undefined }
      );
      setOperationError(err);
      return [];
    }

    const pmResult = await loadPmDailyLogUploads(logId, { projectId, logDate });
    if (pmResult.error) {
      setOperationError(pmResult.error);
      return pmResult.uploads;
    }
    setPmUploads(pmResult.uploads);
    return pmResult.uploads;
  }, [projectId, logDate]);

  const debugSnapshot = useMemo((): DailyLogShareDebugSnapshot => {
    const invalidSub = subcontractorPhotos
      .filter((p) => !subcontractorPhotoValidation.get(p.id)?.valid)
      .map((p) => ({
        id: p.id,
        reason: subcontractorPhotoValidation.get(p.id)?.reason || 'Invalid',
      }));
    const invalidPm = pmUploads
      .filter((p) => !p.valid)
      .map((p) => ({ id: p.id, reason: p.invalidReason || 'Invalid' }));

    return {
      dailyLogId,
      projectId,
      logDate,
      clientVersionStatus,
      clientSharedAt,
      selectedSubcontractorPhotoIds: subcontractorPhotos
        .filter((p) => selectedPhotoIds.has(p.id))
        .map((p) => p.id),
      pmAddedPhotoIds: pmUploads.map((p) => p.id),
      validPhotoCount:
        subcontractorPhotos.filter((p) => subcontractorPhotoValidation.get(p.id)?.valid).length +
        pmUploads.filter((p) => p.valid).length,
      invalidPhotoCount: invalidSub.length + invalidPm.length,
      invalidPhotoReasons: [...invalidSub, ...invalidPm],
      build: JPH_APP_BUILD_LABEL,
    };
  }, [
    clientSharedAt,
    clientVersionStatus,
    dailyLogId,
    logDate,
    pmUploads,
    projectId,
    selectedPhotoIds,
    subcontractorPhotoValidation,
    subcontractorPhotos,
  ]);

  useEffect(() => {
    if (import.meta.env.DEV) {
      logDailyLogShareDebug('state', debugSnapshot);
    }
  }, [debugSnapshot]);

  useEffect(() => {
    if (!open) {
      setStep('edit');
      setOperationError(null);
      setCopyOk(false);
      setMigrationWarning('');
      setPmUploads([]);
      setPmUploadErrors([]);
      setPmDeleteError('');
      setPmDeletingId(null);
      setInitializing(false);
      setSharing(false);
      setLoading(false);
      setActionMessage(null);
      setShowDebug(false);
      setClientVersionStatus(null);
      setClientSharedAt(null);
      setDailyLogId(null);
      setSelectedPhotoIds(new Set());
      setPmClientComment('');
      setOriginalReport('');
      return;
    }

    let cancelled = false;
    // Never reuse a prior session's dailyLogId across project/date opens.
    setDailyLogId(null);
    setInitializing(true);
    setOperationError(null);
    setMigrationWarning('');
    setActionMessage(null);

    async function init() {
      const resolved = await resolveDailyLogIdForClientShare(projectId, logDate, subcontractorPhotos);
      if (cancelled) return;

      if (resolved.error && !resolved.log) {
        setOperationError(resolved.error);
        setDailyLogId(isValidDailyLogId(resolved.dailyLogId) ? resolved.dailyLogId : null);
        setInitializing(false);
        return;
      }

      const log = resolved.log;
      if (log?.id && isValidDailyLogId(log.id)) {
        setDailyLogId(log.id);
        setOriginalReport(log.management_comment || '');
        setPmClientComment(log.pm_client_comment || '');
        setClientVersionStatus(log.client_version_status ?? null);
        setClientSharedAt(log.client_shared_at ?? null);

        if (log.pm_client_comment === undefined && log.client_version_status === undefined) {
          setMigrationWarning(
            'PM client comment fields are not available yet. Run supabase/migrations/20260706140000_daily_log_client_enrichment.sql before saving drafts or sharing.'
          );
        }

        const defaultSelected = buildDefaultSelectedIds(subcontractorPhotos, log.client_version_status);
        setSelectedPhotoIds(defaultSelected);

        const pmFiles = await refreshPmUploads(log.id);
        if (cancelled) return;

        if (pmFiles.length > 0) {
          setSelectedPhotoIds((prev) => {
            const next = new Set<string>();
            for (const id of prev) {
              if (subcontractorPhotoValidation.get(id)?.valid !== false) next.add(id);
            }
            pmFiles.filter((file) => file.valid).forEach((file) => next.add(file.id));
            return next;
          });
        }
      } else {
        setDailyLogId(isValidDailyLogId(resolved.dailyLogId) ? resolved.dailyLogId : null);
        const captionLines = subcontractorPhotos
          .map((p) => p.caption || p.note)
          .filter(Boolean)
          .join('\n\n');
        setOriginalReport(captionLines || 'No structured subcontractor report found for this day.');
        setPmClientComment('');
        setSelectedPhotoIds(buildDefaultSelectedIds(subcontractorPhotos));
        setPmUploads([]);
        setClientVersionStatus(null);
        setClientSharedAt(null);
      }

      setInitializing(false);
    }

    void init();
    return () => {
      cancelled = true;
    };
  }, [open, projectId, logDate, subcontractorPhotoKey, refreshPmUploads, subcontractorPhotoValidation, subcontractorPhotos]);

  const validPmUploads = pmUploads.filter((p) => p.valid);
  const previewPhotos = useMemo(
    () => [
      ...subcontractorPhotos
        .filter((p) => selectedPhotoIds.has(p.id) && subcontractorPhotoValidation.get(p.id)?.valid)
        .map((p) => ({
          id: p.id,
          url: subcontractorPhotoUrls[p.id] || resolveFileDisplayUrl(p),
          caption: p.caption || p.file_name,
        })),
      ...validPmUploads
        .filter((p) => selectedPhotoIds.has(p.id))
        .map((p) => ({
          id: p.id,
          url: pmUploadDisplayUrls[p.id] || resolveFileDisplayUrl({ file_url: p.url, storage_bucket: p.storage_bucket, storage_path: p.storage_path }),
          caption: p.name,
        })),
    ],
    [subcontractorPhotos, selectedPhotoIds, subcontractorPhotoValidation, validPmUploads, subcontractorPhotoUrls, pmUploadDisplayUrls]
  );

  const shareBlockedReason = useMemo(() => {
    if (sharing) return null;
    if (migrationWarning) return migrationWarning;
    if (pmUploadLoading) return 'Please wait for photo upload to finish.';
    if (!isValidDailyLogId(dailyLogId) && subcontractorPhotos.every((p) => !isValidDailyLogId(p.daily_log_id))) {
      return 'Daily log record is missing for this date.';
    }
    return null;
  }, [sharing, migrationWarning, pmUploadLoading, dailyLogId, subcontractorPhotos]);

  const canPreview =
    !loading &&
    !sharing &&
    !initializing &&
    (selectedPhotoIds.size > 0 || pmClientComment.trim().length > 0 || previewPhotos.length > 0);

  if (!open) return null;

  const previewPhotosList = previewPhotos;

  async function resolveShareDailyLogId(): Promise<string | null> {
    if (isValidDailyLogId(dailyLogId)) return dailyLogId;

    if (dailyLogId != null) {
      setDailyLogId(null);
    }

    logShareQueryDebug('resolveShareDailyLogId', { dailyLogId, projectId, logDate });

    // Resolution order: photo daily_log_id → marker row → project+date fallback
    // (implemented inside resolveDailyLogIdForClientShare). Never treat photo-group
    // id as the daily log row id.
    const resolved = await resolveDailyLogIdForClientShare(projectId, logDate, subcontractorPhotos);
    if (resolved.dailyLogId && isValidDailyLogId(resolved.dailyLogId)) {
      setDailyLogId(resolved.dailyLogId);
      if (resolved.log) {
        setOriginalReport(resolved.log.management_comment || originalReport);
        setPmClientComment((prev) => prev || resolved.log?.pm_client_comment || '');
        setClientVersionStatus(resolved.log.client_version_status ?? null);
        setClientSharedAt(resolved.log.client_shared_at ?? null);
      }
      return resolved.dailyLogId;
    }

    const err =
      resolved.error ||
      shareError(
        'load_daily_log',
        'Daily Log ID could not be resolved. The submission was not sent.'
      );
    setOperationError(err);
    setActionMessage(err.message);
    // eslint-disable-next-line no-console
    console.warn('[DailyLogShare] resolveShareDailyLogId failed — no PATCH will be sent', {
      projectId,
      logDate,
      photoDailyLogIds: subcontractorPhotos.map((p) => p.daily_log_id ?? null),
    });
    return null;
  }

  function withShareTimeout<T>(promise: Promise<T>): Promise<T> {
    return Promise.race([
      promise,
      new Promise<T>((_, reject) => {
        setTimeout(() => {
          reject(
            new Error('Sharing timed out. Your draft was kept. Please try again.')
          );
        }, SHARE_TIMEOUT_MS);
      }),
    ]);
  }

  async function handleCopyError() {
    if (!operationError) return;
    const ok = await copyDailyLogShareError(operationError);
    setCopyOk(ok);
    if (ok) setTimeout(() => setCopyOk(false), 2000);
  }

  async function handlePmPhotoUpload(files: FileList | null) {
    if (!files || files.length === 0) return;
    const logId = await resolveShareDailyLogId();
    if (!logId) return;

    setPmUploadLoading(true);
    setPmUploadErrors([]);
    setOperationError(null);

    const failures: string[] = [];
    const successes: PmDailyLogUploadRow[] = [];

    for (const file of Array.from(files)) {
      const result = await uploadPmDailyLogPhoto({
        file,
        projectId,
        logDate,
        dailyLogId: logId,
        userId: pmUserId,
      });

      if (result.error || !result.fileId || !result.fileUrl) {
        failures.push(formatDailyLogShareError(result.error || {
          operation: 'upload_pm_photo',
          message: 'Failed to upload PM photo',
          fileName: file.name,
          dailyLogId: logId,
        }));
        if (result.error && !operationError) {
          setOperationError(result.error);
        }
        continue;
      }

      successes.push({
        id: result.fileId,
        url: result.fileUrl,
        name: file.name,
        valid: true,
      });
    }

    if (successes.length > 0) {
      setPmUploads((prev) => [...prev, ...successes]);
      setSelectedPhotoIds((prev) => {
        const next = new Set(prev);
        successes.forEach((row) => next.add(row.id));
        return next;
      });
      onPhotosChanged?.();
      await refreshPmUploads(logId);
    }

    if (failures.length > 0) {
      setPmUploadErrors(failures);
    }

    setPmUploadLoading(false);
  }

  async function handleDeletePmPhoto(fileId: string) {
    const logId = await resolveShareDailyLogId();
    if (!logId) return;

    setPmDeletingId(fileId);
    setPmDeleteError('');
    setOperationError(null);

    const result = await deletePmDailyLogPhoto({
      fileId,
      dailyLogId: logId,
      projectId,
      logDate,
    });

    setPmDeletingId(null);

    if (!result.ok) {
      setPmDeleteError(formatDailyLogShareError(result.error));
      setOperationError(result.error);
      return;
    }

    setPmUploads((prev) => prev.filter((p) => p.id !== fileId));
    setSelectedPhotoIds((prev) => {
      const next = new Set(prev);
      next.delete(fileId);
      return next;
    });
    onPhotosChanged?.();
    await refreshPmUploads(logId);
  }

  async function handleSaveDraft() {
    setLoading(true);
    setOperationError(null);
    setActionMessage(null);
    try {
      const logId = await resolveShareDailyLogId();
      if (!logId) return;

      const result = await saveDailyLogClientDraft({
        dailyLogId: logId,
        projectId,
        pmClientComment,
        pmUserId,
        logDate,
      });
      if (!result.ok) {
        setShareError(setOperationError, result.error, 'save_draft', 'Failed to save draft');
        setActionMessage(result.error?.message || 'Failed to save draft');
        return;
      }
      await refreshPmUploads(logId);
      onPhotosChanged?.();
      onSuccess();
      onClose();
    } finally {
      setLoading(false);
    }
  }

  async function handleShareWithClient() {
    // eslint-disable-next-line no-console
    console.debug('[DailyLogShare] Share with Client clicked', {
      build: JPH_APP_BUILD_LABEL,
      projectId,
      logDate,
      dailyLogId,
      step,
    });

    setSharing(true);
    setLoading(true);
    setOperationError(null);
    setActionMessage(null);

    try {
      // eslint-disable-next-line no-console
      console.debug('[DailyLogShare] handler entered (JS path; no native form submit)');

      if (migrationWarning) {
        const err = shareError('share_with_client', migrationWarning);
        setOperationError(err);
        setActionMessage(migrationWarning);
        return;
      }

      if (!pmUserId) {
        const err = shareError('share_with_client', 'You must be signed in to share with the client.');
        setOperationError(err);
        setActionMessage(err.message);
        return;
      }

      if (pmUploadLoading) {
        const err = shareError('share_with_client', 'Please wait for photo upload to finish.');
        setOperationError(err);
        setActionMessage(err.message);
        return;
      }

      const logId = await resolveShareDailyLogId();
      if (!logId) return;

      logShareQueryDebug('handleShareWithClient', { dailyLogId: logId, projectId, logDate });
      // eslint-disable-next-line no-console
      console.debug('[DailyLogShare] share operation started', { dailyLogId: logId, projectId, logDate });

      const latestPmUploads = await refreshPmUploads(logId);
      const pmIds = latestPmUploads.filter((p) => p.valid).map((p) => p.id);

      const pendingLocalPm = pmUploads.filter((p) => p.valid && !pmIds.includes(p.id));
      if (pendingLocalPm.length > 0) {
        const err = shareError(
          'share_with_client',
          'Please wait for PM photo upload to finish before sharing.',
          { dailyLogId: logId, photoId: pendingLocalPm[0]?.id }
        );
        setOperationError(err);
        setActionMessage(err.message);
        return;
      }

      const persistedSelectedIds = Array.from(selectedPhotoIds).filter((id) => {
        const isSub = subcontractorPhotos.some((p) => p.id === id);
        const isPm = pmIds.includes(id);
        if (isSub) return subcontractorPhotoValidation.get(id)?.valid !== false;
        return isPm;
      });

      const result = await withShareTimeout(
        shareDailyLogWithClient({
          dailyLogId: logId,
          projectId,
          logDate,
          pmClientComment,
          pmUserId,
          selectedPhotoIds: persistedSelectedIds,
          allSubcontractorPhotoIds: subcontractorPhotos.map((p) => p.id),
          pmUploadedFileIds: pmIds,
        })
      );

      if (!result.ok) {
        // Keep modal open; surface exact operation error without navigating away.
        setShareError(setOperationError, result.error, 'share_with_client', 'Failed to share with client');
        setActionMessage(result.error?.message || 'Failed to share with client');
        // eslint-disable-next-line no-console
        console.error('[DailyLogShare] share failed', result.error);
        return;
      }

      // eslint-disable-next-line no-console
      console.debug('[DailyLogShare] share completed successfully', { dailyLogId: logId });
      setActionMessage('Shared with client');
      setClientVersionStatus('shared');
      setClientSharedAt(new Date().toISOString());
      onPhotosChanged?.();
      onSuccess();
      onClose();
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'An unexpected error occurred while sharing.';
      const shareErr = shareError('share_with_client', message, { dailyLogId: dailyLogId ?? undefined });
      setOperationError(shareErr);
      setActionMessage(message);
      // eslint-disable-next-line no-console
      console.error('[DailyLogShare] unexpected share error', err);
    } finally {
      setSharing(false);
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[90] flex items-end md:items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={loading || sharing ? undefined : onClose} />
      <div className="relative bg-white w-full md:max-w-2xl md:mx-4 rounded-t-2xl md:rounded-2xl shadow-2xl max-h-[92vh] flex flex-col">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h3 className="text-[#1A1A1A] font-bold text-base">Prepare for Client</h3>
            <p className="text-xs text-[#6B7280] mt-0.5">{projectName} · {dateLabel}</p>
          </div>
          <button type="button" onClick={onClose} disabled={loading} className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 cursor-pointer">
            <i className="ri-close-line text-[#374151]" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {initializing ? (
            <div className="flex items-center justify-center gap-2 py-16 text-[#9CA3AF] text-sm">
              <i className="ri-loader-4-line animate-spin" />
              Loading subcontractor report...
            </div>
          ) : step === 'edit' ? (
            <>
              <div>
                <p className="text-[10px] font-bold text-[#9CA3AF] uppercase tracking-wider mb-1.5">
                  Original Subcontractor Report (read-only)
                </p>
                <div className="bg-[#F8FAFC] border border-gray-100 rounded-xl px-3 py-2.5">
                  <p className="text-sm text-[#374151] whitespace-pre-wrap leading-relaxed">
                    {originalReport || 'No report text submitted.'}
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-[10px] font-bold text-[#059669] uppercase tracking-wider mb-1.5">
                  PM Client Comment
                </label>
                <textarea
                  value={pmClientComment}
                  onChange={(e) => setPmClientComment(e.target.value)}
                  rows={4}
                  maxLength={2000}
                  placeholder="Add a client-facing comment..."
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm text-[#1A1A1A] resize-none focus:outline-none focus:border-[#059669]"
                />
              </div>

              <div>
                <p className="text-[10px] font-bold text-[#9CA3AF] uppercase tracking-wider mb-2">
                  Subcontractor Photos — include for client
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {subcontractorPhotos.map((photo) => {
                    const validation = subcontractorPhotoValidation.get(photo.id);
                    const isValid = validation?.valid !== false;
                    const included = isValid && selectedPhotoIds.has(photo.id);
                    return (
                      <label
                        key={photo.id}
                        className={`border rounded-xl overflow-hidden ${isValid ? 'cursor-pointer' : 'cursor-not-allowed opacity-70'} ${included ? 'border-[#059669] ring-1 ring-[#059669]' : 'border-gray-100'}`}
                      >
                        <div className="aspect-[4/3] relative bg-[#F3F4F6]">
                          {isValid ? (
                            <ProjectPhotoImage
                              src={subcontractorPhotoUrls[photo.id] || resolveFileDisplayUrl(photo)}
                              alt={photo.caption || photo.file_name}
                              sizes="160px"
                              loading="lazy"
                              debugContext="PrepareDailyLogForClientModal"
                            />
                          ) : (
                            <div className="absolute inset-0 flex items-center justify-center p-2 text-center text-[10px] text-[#9CA3AF]">
                              {validation?.reason || 'Photo unavailable'}
                            </div>
                          )}
                          {isValid && (
                            <div className="absolute top-2 left-2">
                              <input
                                type="checkbox"
                                checked={included}
                                onChange={(e) => {
                                  setSelectedPhotoIds((prev) => {
                                    const next = new Set(prev);
                                    if (e.target.checked) next.add(photo.id);
                                    else next.delete(photo.id);
                                    return next;
                                  });
                                }}
                                className="w-4 h-4 accent-[#059669]"
                              />
                            </div>
                          )}
                        </div>
                        <p className="text-[10px] text-[#6B7280] px-2 py-1 line-clamp-2">
                          {isValid ? photo.caption || photo.file_name : validation?.reason}
                        </p>
                      </label>
                    );
                  })}
                </div>
              </div>

              <div>
                <p className="text-[10px] font-bold text-[#9CA3AF] uppercase tracking-wider mb-2">PM Additional Photos</p>
                <InlineUploadDropzone
                  accept="image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp"
                  multiple
                  allowCamera
                  disabled={initializing || !dailyLogId}
                  loading={pmUploadLoading}
                  error={pmUploadErrors[0] || ''}
                  title="Drop PM photos here or click to choose"
                  hint={!dailyLogId ? 'Waiting for subcontractor daily log record…' : 'JPG, PNG, WEBP only · max 15 MB · not shared until you share'}
                  onFilesSelected={(selected) => void handlePmPhotoUpload(selected)}
                />
                {pmUploadErrors.length > 1 && (
                  <div className="mt-2 space-y-1">
                    {pmUploadErrors.slice(1).map((msg) => (
                      <div key={msg} className="bg-[#FEF2F2] rounded-xl px-3 py-2 text-[#EF4444] text-xs whitespace-pre-wrap">
                        {msg}
                      </div>
                    ))}
                  </div>
                )}
                {pmUploads.length > 0 && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2">
                    {pmUploads.map((p) => {
                      const included = p.valid && selectedPhotoIds.has(p.id);
                      return (
                        <div
                          key={p.id}
                          className={`relative border rounded-xl overflow-hidden ${included ? 'border-[#059669] ring-1 ring-[#059669]' : 'border-gray-100'} ${!p.valid ? 'opacity-70' : ''}`}
                        >
                          <div className="aspect-[4/3] relative bg-[#F3F4F6]">
                            {p.valid && (pmUploadDisplayUrls[p.id] || p.url) ? (
                              <img
                                src={pmUploadDisplayUrls[p.id] || resolveFileDisplayUrl({ file_url: p.url, storage_bucket: p.storage_bucket, storage_path: p.storage_path })}
                                alt={p.name}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="absolute inset-0 flex items-center justify-center p-2 text-center text-[10px] text-[#9CA3AF]">
                                {p.invalidReason || 'Photo unavailable'}
                              </div>
                            )}
                            {p.valid && (
                              <div className="absolute top-2 left-2">
                                <input
                                  type="checkbox"
                                  checked={included}
                                  onChange={(e) => {
                                    setSelectedPhotoIds((prev) => {
                                      const next = new Set(prev);
                                      if (e.target.checked) next.add(p.id);
                                      else next.delete(p.id);
                                      return next;
                                    });
                                  }}
                                  className="w-4 h-4 accent-[#059669]"
                                />
                              </div>
                            )}
                            <div className="absolute top-2 right-2">
                              <span className="px-1.5 py-0.5 rounded-full bg-[#059669] text-white text-[9px] font-bold">
                                PM photo
                              </span>
                            </div>
                            <button
                              type="button"
                              onClick={() => void handleDeletePmPhoto(p.id)}
                              disabled={pmDeletingId === p.id || loading}
                              className="absolute bottom-2 right-2 w-7 h-7 rounded-full bg-black/60 text-white flex items-center justify-center cursor-pointer disabled:opacity-50"
                              title="Remove PM photo"
                            >
                              {pmDeletingId === p.id ? (
                                <i className="ri-loader-4-line animate-spin text-xs" />
                              ) : (
                                <i className="ri-delete-bin-line text-xs" />
                              )}
                            </button>
                          </div>
                          <p className="text-[10px] text-[#6B7280] px-2 py-1 line-clamp-2">{p.name}</p>
                        </div>
                      );
                    })}
                  </div>
                )}
                {pmDeleteError && (
                  <div className="mt-2 bg-[#FEF2F2] rounded-xl px-3 py-2 text-[#EF4444] text-xs whitespace-pre-wrap">{pmDeleteError}</div>
                )}
              </div>
            </>
          ) : (
            <>
              <div>
                <p className="text-[10px] font-bold text-[#9CA3AF] uppercase tracking-wider mb-1.5">Client Preview</p>
                <div className="space-y-3">
                  <div className="bg-[#F8FAFC] rounded-xl px-3 py-2.5 border border-gray-100">
                    <p className="text-[10px] font-bold text-[#6B7280] mb-1">Site report</p>
                    <p className="text-sm whitespace-pre-wrap text-[#374151]">{originalReport || '—'}</p>
                  </div>
                  {pmClientComment.trim() && (
                    <div className="bg-[#F0FDF4] rounded-xl px-3 py-2.5 border border-[#BBF7D0]">
                      <p className="text-[10px] font-bold text-[#059669] mb-1">PM comment</p>
                      <p className="text-sm whitespace-pre-wrap text-[#374151]">{pmClientComment}</p>
                    </div>
                  )}
                </div>
              </div>
              <div>
                <p className="text-[10px] font-bold text-[#9CA3AF] uppercase tracking-wider mb-2">
                  Photos client will see ({previewPhotosList.length})
                </p>
                {previewPhotosList.length === 0 ? (
                  <p className="text-xs text-[#9CA3AF]">No photos selected. Client will see the report and PM comment only.</p>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {previewPhotosList.map((photo) => (
                      <div key={photo.id} className="border border-gray-100 rounded-xl overflow-hidden">
                        {photo.url ? (
                          <img src={photo.url} alt={photo.caption || 'Photo'} className="aspect-[4/3] w-full object-cover" />
                        ) : (
                          <div className="aspect-[4/3] flex items-center justify-center text-[10px] text-[#9CA3AF] p-2 text-center">
                            Photo unavailable
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}

          {migrationWarning && (
            <div className="bg-[#FFFBEB] rounded-xl px-3 py-2.5 text-[#92400E] text-xs border border-[#FDE68A]">
              {migrationWarning}
            </div>
          )}

          {operationError && (
            <div className="bg-[#FEF2F2] rounded-xl px-3 py-3 text-[#EF4444] text-xs border border-[#FECACA] space-y-2">
              <p className="font-bold uppercase tracking-wide text-[10px]">
                {formatOperationLabel(operationError.operation)} failed
              </p>
              <pre className="whitespace-pre-wrap font-sans text-xs leading-relaxed">
                {formatDailyLogShareError(operationError)}
              </pre>
              {operationError.recoveryHint && (
                <p className="text-[#B45309]">{operationError.recoveryHint}</p>
              )}
              <button
                type="button"
                onClick={() => void handleCopyError()}
                className="px-2.5 py-1 rounded-lg bg-white border border-[#FECACA] text-[#EF4444] text-[10px] font-semibold cursor-pointer"
              >
                {copyOk ? 'Copied' : 'Copy error details'}
              </button>
            </div>
          )}

          <div className="border border-dashed border-gray-200 rounded-xl">
            <button
              type="button"
              onClick={() => setShowDebug((v) => !v)}
              className="w-full px-3 py-2 text-left text-[10px] font-semibold text-[#6B7280] cursor-pointer"
            >
              {showDebug ? 'Hide' : 'Show'} technical details
            </button>
            {showDebug && (
              <pre className="px-3 pb-3 text-[10px] text-[#6B7280] whitespace-pre-wrap overflow-x-auto">
                {JSON.stringify(debugSnapshot, null, 2)}
              </pre>
            )}
          </div>
        </div>

        <div className="px-5 py-4 border-t border-gray-100 flex-shrink-0 space-y-2">
          {(operationError || actionMessage) && (
            <div
              className={`rounded-xl px-3 py-2.5 text-xs border ${
                actionMessage === 'Shared with client'
                  ? 'bg-[#F0FDF4] text-[#059669] border-[#86EFAC]/40'
                  : 'bg-[#FEF2F2] text-[#EF4444] border-[#FECACA]'
              }`}
            >
              {operationError ? (
                <>
                  <p className="font-bold uppercase tracking-wide text-[10px] mb-1">
                    {formatOperationLabel(operationError.operation)} failed
                  </p>
                  <p className="whitespace-pre-wrap">{formatDailyLogShareError(operationError)}</p>
                </>
              ) : (
                <p>{actionMessage}</p>
              )}
            </div>
          )}
          {shareBlockedReason && step === 'preview' && !operationError && (
            <p className="text-xs text-[#92400E] bg-[#FFFBEB] border border-[#FDE68A] rounded-xl px-3 py-2">
              {shareBlockedReason}
            </p>
          )}
          <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={loading || sharing}
            className="px-4 py-2.5 rounded-xl border border-gray-200 text-[#374151] text-sm font-semibold cursor-pointer"
          >
            Cancel
          </button>
          {step === 'edit' ? (
            <>
              <button
                type="button"
                onClick={() => void handleSaveDraft()}
                disabled={loading || initializing}
                className="px-4 py-2.5 rounded-xl bg-gray-100 text-[#374151] text-sm font-semibold cursor-pointer disabled:opacity-50"
              >
                {loading ? 'Saving…' : 'Save Draft'}
              </button>
              <button
                type="button"
                onClick={() => setStep('preview')}
                disabled={!canPreview}
                className="ml-auto px-4 py-2.5 rounded-xl bg-[#E8621A] text-white text-sm font-semibold cursor-pointer disabled:opacity-50"
              >
                Preview for Client
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={() => setStep('edit')}
                disabled={loading}
                className="px-4 py-2.5 rounded-xl border border-gray-200 text-[#374151] text-sm font-semibold cursor-pointer"
              >
                Back / Edit
              </button>
              <button
                type="button"
                onClick={() => void handleShareWithClient()}
                disabled={loading || sharing}
                className="ml-auto px-4 py-2.5 rounded-xl bg-[#059669] text-white text-sm font-semibold cursor-pointer disabled:opacity-50 flex items-center gap-2"
              >
                {sharing ? (
                  <>
                    <i className="ri-loader-4-line animate-spin" />
                    Sharing…
                  </>
                ) : (
                  <>
                    <i className="ri-share-forward-line" />
                    Share with Client
                  </>
                )}
              </button>
            </>
          )}
          </div>
        </div>
      </div>
    </div>
  );
}
